/**
 * @file MobileMenuPreview.tsx
 * @description Как меню сложится на телефоне (phase19.16 Р1; SB15 — мобильное
 * меню собирается само; uiux-design-r1 §3 «Меню телефона»): пункты шапки
 * (вложенные — списком, с «Все в «…»», если у пункта есть своя ссылка),
 * пункты подвала, вход в кабинет (ставит платформа), контакты с «в меню
 * телефона». Скрытые и снятые с публикации пункты не показываются.
 */

import { SITE_CONTACT_KINDS, type SiteContactItem } from "@chaiberi/shared";
import { ChevronDown, X } from "lucide-react";
import type { ReactNode } from "react";

import type { SiteMenuItem } from "../../../lib/site-view/types";

/** Пункт виден на сайте: не скрыт и цель на месте (SB16, SB81). */
function visible(item: SiteMenuItem): boolean {
  return !item.isHidden && item.target.status !== "unpublished" && item.target.status !== "deleted";
}

interface Props {
  siteName: string;
  header: SiteMenuItem[];
  footer: SiteMenuItem[];
  contacts: SiteContactItem[];
}

function Row({ children, sub = false }: { children: ReactNode; sub?: boolean }) {
  return (
    <div className={sub ? "flex min-h-10 items-center pl-4 text-sm text-on-surface-variant" : "flex min-h-12 items-center justify-between text-sm text-on-surface"}>
      {children}
    </div>
  );
}

export function MobileMenuPreview({ siteName, header, footer, contacts }: Props) {
  const top = header.filter(visible);
  const bottom = footer.filter(visible).flatMap((col) => (col.target.href ? [col] : col.children.filter(visible)));
  const inMenu = contacts.filter((c) => c.showInMenu);

  return (
    <div className="flex w-full max-w-[22.5rem] flex-col rounded-xl bg-surface-lowest p-4 [box-shadow:inset_0_0_0_1px_var(--border)]" aria-label="Меню телефона, 360 пикселей">
      <div className="flex min-h-12 items-center justify-between border-b border-[var(--border)]">
        <span className="type-card-title text-on-surface">{siteName}</span>
        <X aria-hidden className="size-5 text-on-surface-variant" />
      </div>
      {top.map((item) => {
        const kids = item.children.filter(visible);
        return (
          <div key={item.id} className="flex flex-col">
            <Row>
              {item.label ?? item.target.title}
              {kids.length > 0 ? <ChevronDown aria-hidden className="size-4" /> : null}
            </Row>
            {kids.length > 0 && item.target.href ? <Row sub>Все в «{item.label ?? item.target.title}»</Row> : null}
            {kids.map((kid) => (
              <Row key={kid.id} sub>
                {kid.label ?? kid.target.title}
              </Row>
            ))}
          </div>
        );
      })}
      {bottom.length > 0 ? <hr className="my-1 border-[var(--border)]" /> : null}
      {bottom.map((item) => (
        <Row key={item.id}>{item.label ?? item.target.title}</Row>
      ))}
      <hr className="my-1 border-[var(--border)]" />
      <Row>Войти / Мой кабинет</Row>
      {inMenu.length > 0 ? (
        <p className="type-caption py-2 text-on-surface-variant">
          {inMenu.map((c) => (c.kind === "phone" ? c.value : SITE_CONTACT_KINDS[c.kind].label)).join(" · ")}
        </p>
      ) : null}
    </div>
  );
}
